'use client';

import { useCallback } from 'react';

import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';

import { PRODUCT_IMAGE_PATH } from '@shared/config/paths/paths';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { Button } from '@shared/ui/Button';
import { Container } from '@shared/ui/Container';
import { JsonLd } from '@shared/ui/JsonLd';
import { Pagination } from '@shared/ui/Pagination';
import { VStack } from '@shared/ui/Stack';
import { ProductCard } from '@widgets/ProductCard';

import { changePage, showMore } from '../../model/slice/categorySlice';
import cls from './Products.module.scss';

export const Products = () => {
  const dispatch = useAppDispatch();
  const view = useAppSelector((state) => state.category.view);
  const products = useAppSelector((state) => state.category.products);
  const pagination = useAppSelector((state) => state.category.pagination);
  const showMoreButtonLoading = useAppSelector((state) => state.category.showMoreButtonLoading);

  const pathname = usePathname();

  const showMoreProducts = () => {
    dispatch(showMore());
  };

  const changePageNumber = useCallback(
    (page: number) => {
      dispatch(changePage(page));
    },
    [dispatch]
  );

  return (
    <>
      <div className={cls.wrap}>
        <Container>
          {products.length > 0 ? (
            <div className={clsx(cls.list, { [cls.row]: view === 'row' })}>
              {products.map((product, i) => (
                <ProductCard
                  key={i}
                  data={product}
                  view={view}
                />
              ))}
            </div>
          ) : (
            <VStack className={cls.emptyState}>
              <h2 className={cls.emptyStateTitle}>К сожалению, у нас нет таких товаров</h2>
              <div className={cls.emptyStateText}>Попробуйте поискать что-то другое или посмотрите раздел ниже</div>
            </VStack>
          )}

          {pagination.page < pagination.pageAll && (
            <Button
              loadingButton
              isLoading={showMoreButtonLoading}
              onClick={showMoreProducts}
              fullWidth
              variant={'secondary'}
              className={cls.more}
              testId={'show-more-btn'}
            >
              Показать еще
            </Button>
          )}
          {pagination.total > 0 && (
            <Pagination
              changePageNumber={changePageNumber}
              className={cls.pagination}
              data={pagination}
            />
          )}
        </Container>
      </div>
      <JsonLd
        html={{
          '@type': 'ItemList',
          url: `${process.env.NEXT_PUBLIC_SITE_URL}${pathname}`,
          numberOfItems: products.length,
          itemListElement: products.map((product) => {
            const image = product.images?.[0]?.value;
            return {
              '@type': 'Product',
              image: image ? `${process.env.NEXT_PUBLIC_SITE_URL}${PRODUCT_IMAGE_PATH}/${image}/600.webp` : undefined,
              name: product.name,
              url: `${process.env.NEXT_PUBLIC_SITE_URL}${pathname}${products[0].url}`,
              offers: {
                '@type': 'Offer',
                priceCurrency: 'RUB',
                price: product.price
              }
            };
          })
        }}
      />
    </>
  );
};
